import React, { Suspense, useEffect, useState } from 'react'
import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import FallbackSpinner from './@core/components/spinner'
import AuthGuard from 'src/@core/components/auth/AuthGuard'
import UserLayout from './layouts/UserLayout'
import BlankLayout from './@core/layouts/BlankLayout'
import AclGuard from './@core/components/auth/AclGuard'
import GuestGuard from './@core/components/auth/GuestGuard'
import { defaultACLObj } from './configs/acl'
import AuthLayout from './layouts/AuthLayout'
import BlankLayoutWithAppBar from './@core/layouts/BlankLayoutWithAppBar'
import { Box, Typography } from '@mui/material'
import { useAuth } from './hooks/useAuth'
import toast from 'react-hot-toast'
import { getNotificationPermission, messaging } from './notification/firebase'
import { onMessage } from 'firebase/messaging'
import BellOutline from 'mdi-material-ui/BellOutline'
import CookieConsentBanner from 'src/components/CookieConsentBanner'
import GoodsCategoryLabelsBootstrap from 'src/components/GoodsCategoryLabelsBootstrap'
import { CurrencyContext } from 'src/context/CurrancyContext'
import ProtectedRoute from './views/common/ProtectedRoute'
import SetPasswordPage from './pages/setPassword'

const PRIVACY_POLICY_URL =
  process.env.REACT_APP_PRIVACY_POLICY_URL || 'https://www.oujat.com/privacy-policy-page'

const LoginPage = React.lazy(() => import('./pages/login'))
const RegisterPage = React.lazy(() => import('./pages/register'))
const PaymentFailed = React.lazy(() => import('./pages/paymentFailed'))
const PaymentSuccess = React.lazy(() => import('./pages/paymentSuccess'))
const VerificationUnderProcess = React.lazy(() => import('./pages/verificationprocess'))
const ForgotPasswordPage = React.lazy(() => import('./pages/forgotPassword'))
const VerifyOtpPage = React.lazy(() => import('./pages/verifyOtp'))
const ResetPasswordPage = React.lazy(() => import('./pages/resetPassword'))
const HomePage = React.lazy(() => import('./pages/home'))
const UserPage = React.lazy(() => import('./pages/user'))
const DashBoardPage = React.lazy(() => import('./pages/dashboard'))
const InternationalTransportServicePage = React.lazy(() => import('./pages/international-transport-service'))
const QmsPage = React.lazy(() => import('./pages/qms'))
const QmsDetailPage = React.lazy(() => import('./pages/qms/qms-detail'))
const QmsFormPage = React.lazy(() => import('./pages/qms/quotation-form'))
const CreateNewQuotation = React.lazy(() => import('./pages/open-quate/create-new-quotation'))
const QuoteDetailPage = React.lazy(() => import('./pages/qms/quote-detail'))
const QmsFormOverviewPage = React.lazy(() => import('./pages/qms/quotation-form-overview'))
const OpenQuoteRequestPage = React.lazy(() => import('./pages/open-quate'))
const OpenQuoteDetailPage = React.lazy(() => import('./pages/open-quate/open-quote-detail'))
const ServiceCategoryOptionList = React.lazy(
  () => import('./pages/international-transport-service/ServiceCategoryOptionList')
)
const ServicePage = React.lazy(() => import('./pages/service'))
const WithProtectionServicedetail = React.lazy(() => import('./pages/service/withprotectionservicedetail'))
const WithoutProtectionServicedetail = React.lazy(() => import('./pages/service/withoutprotectionservicedetail'))
const StoreFrontPage = React.lazy(() => import('./pages/storefront'))
const AddstorePage = React.lazy(() => import('./pages/storefront/addstore'))
const TeamManagementPage = React.lazy(() => import('./pages/team-management'))
const AssignTeamRole = React.lazy(() => import('./pages/assign-role'))
const AssignPermissions = React.lazy(() => import('./pages/assign-permissions'))
const SubscriptionManagementPage = React.lazy(() => import('./pages/subscriptions'))
const OrdersPage = React.lazy(() => import('./pages/orders'))
const OrderOverview = React.lazy(() => import('./pages/orders/order-overview'))
const OrderQuoteFormPage = React.lazy(() => import('./pages/orders/quotation-form'))
const AssesmentQuoteDetailPage = React.lazy(() => import('./pages/orders/assesment-quote-details'))
const OrderCancelledPage = React.lazy(() => import('./pages/orders/order-cancelled'))
const ViewChargesPage = React.lazy(() => import('./pages/orders/view-charges'))
const OrderCompletedPage = React.lazy(() => import('./pages/orders/order-completed'))
const PaymentsPage = React.lazy(() => import('./pages/payments'))
const Page401 = React.lazy(() => import('./pages/401'))
const Page404 = React.lazy(() => import('./pages/404'))
const OujatProtectionPage = React.lazy(() => import('./pages/oujatprotection'))
const ProfilePage = React.lazy(() => import('./pages/profile'))
const DisputeOrderList = React.lazy(() => import('./pages/dispute-center/DisputeOrderList'))
const DisputeCenterPage = React.lazy(() => import('./pages/dispute-center'))
const AddressBookPage = React.lazy(() => import('./pages/address-book/AddressBook'))
const AddPersonalContactPage = React.lazy(() => import('./pages/address-book/contact-type/PersonalContact'))
const AddBusinessContactPage = React.lazy(() => import('./pages/address-book/contact-type/BusinessContact'))
const NotificationPage = React.lazy(() => import('./pages/notifications'))
const ChatPage = React.lazy(() => import('./pages/chat'))
const BadgeManagementPage = React.lazy(() => import('./pages/badge-management'))
const HelpChatPage = React.lazy(() => import('./pages/helpChat'))

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return <GuestGuard fallback={<FallbackSpinner />}>{children}</GuestGuard>
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>
  } else {
    return <AuthGuard fallback={<FallbackSpinner />}>{children}</AuthGuard>
  }
}

const PendingRestrictionGuard = ({ children, feature }) => {
  const { user } = useAuth()
  const isPendingApproval = Boolean(user?.isReadOnly)
  const isRestrictedFeature = feature === 'quotation' || feature === 'storefront'
  if (isPendingApproval && isRestrictedFeature) {
    return <Navigate to='/dashboard' replace />
  }
  return children
}

function App() {
  const aclAbilities = defaultACLObj
  const [currency, setCurrency] = React.useState('USD')
  const { setFcmToken } = useAuth()
  useEffect(() => {
    getNotificationPermission(setFcmToken)
    onMessage(messaging, payload => {
      // getUnReadNotificationCount()
      toast(
        t => (
          <Box display={'flex'} alignItems={'flex-start'} gap={3}>
            <BellOutline />
            <Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, lineHeight: '25px', textTransform: 'capitalize' }}>
                {payload.notification.title}
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '15px' }}>
                {payload.notification.body}
              </Typography>
            </Box>
          </Box>
        ),
        {
          style: { border: '1px solid #FB3601', borderRadius: '12px' },
          position: 'top-right',
          duration: 3000
        }
      )
    })
  }, [])
  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
      <>
        <Suspense fallback={<FallbackSpinner />}>
          <AclGuard aclAbilities={aclAbilities}>
            <Routes>
            <Route
              element={
                <BlankLayout>
                  <Outlet />
                </BlankLayout>
              }
            >
              <Route path='/401' element={<Page401 />} />
              <Route path='/404' element={<Page404 />} />
              <Route path='/payment-success' element={<PaymentSuccess />} />
              <Route path='/payment-cancel' element={<PaymentFailed />} />
              <Route path='/help' element={<HelpChatPage />} />
              <Route path='/set-password' element={<SetPasswordPage />} />

              <Route
                element={
                  <AuthLayout>
                    <Outlet />
                  </AuthLayout>
                }
              >
                <Route
                  element={
                    <Guard guestGuard>
                      <Outlet />
                    </Guard>
                  }
                >
                  <Route path='/login' element={<LoginPage />}></Route>
                  <Route path='/register' element={<RegisterPage />}></Route>
                  <Route path='/verificationprocess' element={<VerificationUnderProcess />}></Route>
                  <Route path='/forgot-password' element={<ForgotPasswordPage />}></Route>
                  <Route path='/verify-otp' element={<VerifyOtpPage />}></Route>
                  <Route path='/reset-password' element={<ResetPasswordPage />} />
                </Route>
              </Route>
            </Route>

            <Route
              element={
                <Guard authGuard>
                  <Outlet />
                </Guard>
              }
            >
              <Route
                element={
                  <BlankLayoutWithAppBar>
                    <Outlet />
                  </BlankLayoutWithAppBar>
                }
              >
                <Route path='/service/add' element={<InternationalTransportServicePage />}></Route>
              </Route>
              <Route
                element={
                  <UserLayout>
                    <Outlet />
                  </UserLayout>
                }
              >
                <Route
                  path=''
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route path='/dashboard' element={<DashBoardPage />} />
                <Route
                  path='/profile'
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/service/international-transport-service'
                  element={
                    <ProtectedRoute>
                      <InternationalTransportServicePage />
                    </ProtectedRoute>
                  }
                ></Route>
                <Route
                  path='/user'
                  element={
                    <ProtectedRoute>
                      <UserPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/my-qms'
                  element={
                    <ProtectedRoute>
                      <QmsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/my-qms/:id'
                  element={
                    <ProtectedRoute>
                      <QmsDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/my-qms/quote/:id'
                  element={
                    <ProtectedRoute>
                      <QuoteDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/my-qms/quotation-form/:id'
                  element={
                    <ProtectedRoute>
                      <PendingRestrictionGuard feature='quotation'>
                        <QmsFormPage />
                      </PendingRestrictionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/my-qms/quotation-overview'
                  element={
                    <ProtectedRoute>
                      <PendingRestrictionGuard feature='quotation'>
                        <QmsFormOverviewPage />
                      </PendingRestrictionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/open-quote'
                  element={
                    <ProtectedRoute>
                      <OpenQuoteRequestPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/open-quote/create-new-quotation'
                  element={
                    <ProtectedRoute>
                      <PendingRestrictionGuard feature='quotation'>
                        <CreateNewQuotation />
                      </PendingRestrictionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/open-quote/quotation-form'
                  element={
                    <ProtectedRoute>
                      <PendingRestrictionGuard feature='quotation'>
                        <OpenQuoteDetailPage />
                      </PendingRestrictionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/open-quote/:id'
                  element={
                    <ProtectedRoute>
                      <OpenQuoteDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/service'
                  element={
                    <ProtectedRoute>
                      <ServicePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/service/selected-category'
                  element={
                    <ProtectedRoute>
                      <ServiceCategoryOptionList />
                    </ProtectedRoute>
                  }
                ></Route>
                <Route
                  path='/service/:id/edit'
                  element={
                    <ProtectedRoute>
                      <InternationalTransportServicePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/service/with-protections-service-details/:id'
                  element={
                    <ProtectedRoute>
                      <WithProtectionServicedetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/service/without-protections-service-details/:id'
                  element={
                    <ProtectedRoute>
                      <WithoutProtectionServicedetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/store-front'
                  element={
                    <ProtectedRoute>
                      <StoreFrontPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/store-front/add-store'
                  element={
                    <ProtectedRoute>
                      <PendingRestrictionGuard feature='storefront'>
                        <AddstorePage />
                      </PendingRestrictionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/team-management'
                  element={
                    <ProtectedRoute>
                      <TeamManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/team-management/assign-role'
                  element={
                    <ProtectedRoute>
                      <AssignTeamRole />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/team-management/assign-role/:id'
                  element={
                    <ProtectedRoute>
                      <AssignTeamRole />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/team-management/assign-permissions'
                  element={
                    <ProtectedRoute>
                      <AssignPermissions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/team-management/assign-permissions/:id'
                  element={
                    <ProtectedRoute>
                      <AssignPermissions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/order'
                  element={
                    <ProtectedRoute>
                      <OrdersPage />
                    </ProtectedRoute>
                  }
                />{' '}
                <Route
                  path='/order/quotation/:id'
                  element={
                    <ProtectedRoute>
                      <PendingRestrictionGuard feature='quotation'>
                        <OrderQuoteFormPage />
                      </PendingRestrictionGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/order/order-overview/:id'
                  element={
                    <ProtectedRoute>
                      <OrderOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/order/order-overview/assesment-quote-details/:id'
                  element={
                    <ProtectedRoute>
                      <AssesmentQuoteDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/order/order-cancelled/:id'
                  element={
                    <ProtectedRoute>
                      <OrderCancelledPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/order/order-cancelled/view-charges'
                  element={
                    <ProtectedRoute>
                      <ViewChargesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/order/order-completed/:id'
                  element={
                    <ProtectedRoute>
                      <OrderCompletedPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/payments'
                  element={
                    <ProtectedRoute>
                      <PaymentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/subscriptions'
                  element={
                    <ProtectedRoute>
                      <SubscriptionManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/oujat-protection'
                  element={
                    <ProtectedRoute>
                      <OujatProtectionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/dispute-center'
                  element={
                    <ProtectedRoute>
                      <DisputeOrderList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/dispute-center/create-dispute'
                  element={
                    <ProtectedRoute>
                      <DisputeCenterPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/address-book'
                  element={
                    <ProtectedRoute>
                      <AddressBookPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/address-book/add-personal-contact'
                  element={
                    <ProtectedRoute>
                      <AddPersonalContactPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/address-book/add-business-contact'
                  element={
                    <ProtectedRoute>
                      <AddBusinessContactPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/badge-management'
                  element={
                    <ProtectedRoute>
                      <BadgeManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/notification'
                  element={
                    <ProtectedRoute>
                      <NotificationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/chat'
                  element={
                    <ProtectedRoute>
                      <ChatPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Route>

            {/* If no route found redirect it to the --> /404 */}
            <Route path='*' element={<Navigate to='/404' replace />} />
          </Routes>
        </AclGuard>
      </Suspense>
      <CookieConsentBanner privacyHref={PRIVACY_POLICY_URL} />
      <GoodsCategoryLabelsBootstrap />
    </>
    </CurrencyContext.Provider>
  )
}

export default App
